import { useQuery } from "@tanstack/react-query";
import {
  getGeneralStatus,
  getHomeScreenDashboardV3,
  getHomeScreenV2,
  getOverview,
  getTodaysTickets,
  getTotalService,
  getTotalWorkHours,
  getUpcomingActivities,
} from "controller/api";
import type {
  GetHomeScreenDashboardV3Input,
  getHomeScreenV2Input,
} from "shared";
import { debugConsole } from "utilz/helpers";

export const useGetUpcomingActivities = (resourceId?: string | null) => {
  return useQuery({
    queryKey: ["home_screen", "upcoming_activities", resourceId],
    queryFn: () => getUpcomingActivities(resourceId || ""),
    enabled: !!resourceId,
    onError(err) {
      debugConsole("useGetUpcomingActivities", err);
    },
  });
};

export const useGetGeneralStatus = (filter: string,timezone:string) => {
  return useQuery({
    queryKey: ["home_screen", "general_status", filter,timezone],
    queryFn: () => getGeneralStatus(filter,timezone),
    onError(err) {
      debugConsole("useGetGeneralStatus", err);
    },
  });
};

export const useGetOverview = (resourceId: string, filter: string,timezone:string) => {
  return useQuery({
    queryKey: ["home_screen", "overview", resourceId, filter,timezone],
    enabled: !!resourceId && `${resourceId}`.trim().length > 0,
    queryFn: () => getOverview(resourceId, filter,timezone),
    onError(err) {
      debugConsole("useGetOverview", err);
    },
  });
};

export const useGetTotalWorkHours = (resourceId: string, filter: string,timezone:string) => {
  return useQuery({
    queryKey: ["home_screen", "total_work_hours", resourceId, filter,timezone],
    queryFn: () => getTotalWorkHours(resourceId, filter,timezone),
    enabled: !!resourceId,
    onError(err) {
      debugConsole("useGetTotalWorkHours", err);
    },
  });
};

export const useGetTotalService = (resourceId: string, filter: string,timezone:string) => {
  return useQuery({
    queryKey: ["home_screen", "total_services", resourceId, filter,timezone],
    queryFn: () => getTotalService(resourceId, filter,timezone),
    enabled: !!resourceId,
    onError(err) {
      debugConsole("useGetTotalService", err);
    },
  });
};

export const useGetTodaysTickets = (timezone:string) => {
  return useQuery({
    queryKey: ["home_screen", "todays_tickets",timezone],
    queryFn: () => getTodaysTickets(timezone),
    onError(err) {
      debugConsole("useGetTodaysTickets", err);
    },
  });
};

export const useGetHomeScreenV2 = ({
  resourceId,
  filter,
  timezone,
}: getHomeScreenV2Input) => {
  return useQuery({
    queryKey: ["home_screen_dashboard", resourceId, filter, timezone],
    queryFn: () => getHomeScreenV2({ resourceId, filter, timezone }),
    // The resource id is a path segment — firing without one hits a malformed
    // URL, so hold until the caller's scope resolves.
    enabled: !!resourceId,
  });
};

export const useGetHomeScreenDashboardV3 = (
  data: GetHomeScreenDashboardV3Input,
  /** Lets a caller hold the request until its resource selection resolves. */
  enabled = true,
) => {
  return useQuery({
    queryKey: [
      "home_screen_dashboard_v3",
      data.filter,
      data.timezone,
      data.all ? "all" : (data.resource_ids ?? []).join(","),
    ],
    queryFn: () => getHomeScreenDashboardV3(data),
    // An empty selection is a 400, not "everything" — never send one.
    enabled: enabled && (!!data.all || !!data.resource_ids?.length),
    onError(err) {
      debugConsole("useGetHomeScreenDashboardV3", err);
    },
  });
};
